import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import classNames from "classnames";

import useDynamicAutocompleteSuggestions from "../../../../../hooks/useDynamicAutocompleteSuggestions";
import useScrollActiveOptionIntoView from "../../../../../hooks/useScrollActiveOptionIntoView";

import "../rendererStyles/Heading.scss";

const POPOVER_WIDTH = 200;
const POPOVER_EDGE_OFFSET = 8;
const POPOVER_ANCHOR_OFFSET = 4;

export const getHeadingSearchPopoverPosition = anchorElement => {
  if (!anchorElement) {
    return { top: 0, left: 0 };
  }

  const rect = anchorElement.getBoundingClientRect();
  const top = rect.bottom + POPOVER_ANCHOR_OFFSET;
  const maxLeft = window.innerWidth - POPOVER_WIDTH - POPOVER_EDGE_OFFSET;
  const left = Math.max(
    POPOVER_EDGE_OFFSET,
    Math.min(rect.left, maxLeft)
  );

  return { top, left };
};

const formatSuggestionLabel = suggestion =>
  suggestion.name
    ? `${suggestion.ticker} - ${suggestion.name}`
    : suggestion.ticker;

const HeadingSearchPopover = ({
  anchorRef,
  attributeForFiltering,
  searchValue,
  onSearchValueChange,
  onApply,
  onClose,
  autocomplete = false,
  filterDataUrl
}) => {
  const popoverRef = useRef(null);
  const [position, setPosition] = useState(() =>
    getHeadingSearchPopoverPosition(anchorRef.current)
  );
  const [cursor, setCursor] = useState(0);
  const { listRef, activeOptionRef } = useScrollActiveOptionIntoView(cursor);
  const { phrase, setPhrase, suggestions, loading } =
    useDynamicAutocompleteSuggestions(autocomplete ? filterDataUrl : null);

  const updatePosition = useCallback(() => {
    setPosition(getHeadingSearchPopoverPosition(anchorRef.current));
  }, [anchorRef]);

  useEffect(() => {
    if (autocomplete) {
      setPhrase(searchValue || "");
    }
  }, [autocomplete, searchValue, setPhrase]);

  useEffect(() => {
    setCursor(0);
  }, [suggestions]);

  useEffect(() => {
    updatePosition();

    const handleMouseDown = event => {
      const popover = popoverRef.current;
      const anchor = anchorRef.current;

      if (
        popover?.contains(event.target) ||
        anchor?.contains(event.target)
      ) {
        return;
      }

      onClose();
    };

    const handleKeyDown = event => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    const handleScroll = event => {
      if (popoverRef.current?.contains(event.target)) {
        return;
      }
      onClose();
    };

    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", handleScroll, true);
    document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", handleScroll, true);
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [anchorRef, onClose, updatePosition]);

  const { top, left } = position;
  const inputValue = autocomplete ? phrase : searchValue;

  const selectSuggestion = suggestion => {
    if (!suggestion?.ticker) {
      return;
    }
    onSearchValueChange(suggestion.ticker);
    onApply(suggestion.ticker);
  };

  const handleKeyDown = event => {
    if (!autocomplete) {
      if (event.key === "Enter") {
        onApply();
      }
      return;
    }

    if (event.key === "ArrowDown" && cursor < suggestions.length - 1) {
      event.preventDefault();
      setCursor(cursor + 1);
      return;
    }

    if (event.key === "ArrowUp" && cursor > 0) {
      event.preventDefault();
      setCursor(cursor - 1);
      return;
    }

    if (event.key === "Enter") {
      event.preventDefault();
      if (!phrase.trim()) {
        onApply("");
        return;
      }
      const suggestion = suggestions[cursor];
      if (!suggestion) {
        return;
      }
      selectSuggestion(suggestion);
    }
  };

  return createPortal(
    <div
      ref={popoverRef}
      className={classNames("heading-renderer-search-popover", {
        "heading-renderer-search-popover--autocomplete": autocomplete
      })}
      style={{ top, left }}
    >
      <div className="input-wrapper">
        <input
          type="text"
          id={`search_${attributeForFiltering}`}
          autoFocus
          value={inputValue}
          onChange={event => {
            const next = event.target.value;
            if (autocomplete) {
              setPhrase(next);
            }
            onSearchValueChange(next);
          }}
          onKeyDown={handleKeyDown}
        />
        {!autocomplete ? (
          <i className="fal fa-check apply-search" onClick={() => onApply()} />
        ) : null}
        <i
          className="fal fa-times"
          onClick={() => {
            if (autocomplete) {
              onApply("");
              return;
            }
            onClose();
          }}
        />
      </div>
      {autocomplete && phrase.trim().length > 0 ? (
        <ul
          ref={listRef}
          className="heading-renderer-search-popover__suggestions"
          onMouseDown={event => {
            event.preventDefault();
          }}
        >
          {loading && suggestions.length === 0 ? (
            <li className="heading-renderer-search-popover__empty">…</li>
          ) : null}
          {!loading && suggestions.length === 0 ? (
            <li className="heading-renderer-search-popover__empty">—</li>
          ) : null}
          {suggestions.map((suggestion, index) => (
            <li
              key={`${suggestion.ticker}-${suggestion.market || index}`}
              ref={index === cursor ? activeOptionRef : null}
              className={classNames({
                "is-active": index === cursor
              })}
              onMouseDown={event => {
                event.preventDefault();
                selectSuggestion(suggestion);
              }}
              onMouseEnter={() => setCursor(index)}
            >
              {formatSuggestionLabel(suggestion)}
            </li>
          ))}
        </ul>
      ) : null}
    </div>,
    document.body
  );
};

export default HeadingSearchPopover;
